import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";

// Interface for the bookmark data structure
interface BookmarkNode {
  title: string;
  url?: string;
  children?: BookmarkNode[];
  dateAdded?: number;
  folderType?: boolean;
  level?: number; // Track nesting level
  originalPath?: string; // Full path from root
  id?: string; // Unique identifier for mapping
}

// Interface for a flattened collection structure
interface FlattenedCollection {
  id: string;
  name: string;
  parent: string | null;
  level: number;
  position: number;
  originalPath: string; // Full path to help with mapping
}

// Interface for a content item to be created
interface BookmarkContent {
  title: string;
  url: string;
  collectionId: string;
  dateAdded?: number;
}

// Browser type detection for format-specific parsing
enum BookmarkBrowserType {
  CHROME,
  FIREFOX,
  SAFARI,
  UNKNOWN
}

/**
 * Detects the browser type from the HTML content
 */
const detectBrowserType = (htmlContent: string): BookmarkBrowserType => {
  // Safari bookmarks often lack the add_date attribute that Chrome/Firefox have
  const hasAddDate = htmlContent.includes('add_date=');
  const hasSafariMeta = htmlContent.includes('id="com.apple.ReadingList"') || 
                        htmlContent.includes('TITLE>Bookmarks</TITLE');
  
  if (hasSafariMeta) {
    console.log("Detected Safari bookmark format");
    return BookmarkBrowserType.SAFARI;
  } else if (hasAddDate) {
    console.log("Detected Chrome/Firefox bookmark format");
    return BookmarkBrowserType.CHROME;
  }
  
  console.log("Unable to determine browser format, using generic parsing");
  return BookmarkBrowserType.UNKNOWN;
};

/**
 * Enhanced bookmark parser that accurately handles nested folder structures 
 * with improved DOM traversal to ensure correct parent-child relationships
 */
export const parseBookmarkHtml = (htmlContent: string): BookmarkNode | null => {
  try {
    console.log("Starting enhanced bookmark parsing with improved folder traversal");
    
    // Create a DOM parser
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    
    // Use the actual title from the HTML document as the root name
    let rootTitle = "Bookmarks";
    const titleElement = doc.querySelector("title");
    if (titleElement && titleElement.textContent) {
      rootTitle = titleElement.textContent.trim();
    }
    
    // Create root node based on the document title
    const rootNode: BookmarkNode = {
      title: rootTitle,
      folderType: true,
      children: [],
      level: 0,
      originalPath: rootTitle,
      id: uuidv4() // Assign a unique ID for mapping
    };
    
    console.log(`Starting bookmark parsing with root title: ${rootTitle}`);
    
    // Create maps to store relationships between elements and nodes
    const folderMap = new Map<Element, BookmarkNode>();
    const elementToNodeMap = new Map<Element, BookmarkNode>();
    
    // Process all DL elements first to understand nesting structure
    const dlElements = doc.querySelectorAll("dl");
    console.log(`Found ${dlElements.length} DL elements (potential folder containers)`);
    
    // First, create a mapping of each DL to its parent DL to understand nesting
    const dlParentMap = new Map<Element, Element>();
    dlElements.forEach(dl => {
      let parent = dl.parentElement;
      while (parent) {
        if (parent.tagName === 'DL') {
          dlParentMap.set(dl, parent);
          break;
        }
        parent = parent.parentElement;
      }
    });

    // Process all H3 elements (folder headers)
    const folderHeaders = doc.querySelectorAll("h3");
    console.log(`Found ${folderHeaders.length} folders (H3 elements)`);
    
    // Map each H3 element to a folder node
    folderHeaders.forEach((h3) => {
      const folderName = h3.textContent?.trim() || "Unnamed Folder";
      
      // Create a bookmark node for this folder
      const folderNode: BookmarkNode = {
        title: folderName,
        folderType: true,
        children: [],
        level: 1, // Will be adjusted based on nesting
        originalPath: folderName,
        dateAdded: h3.getAttribute("add_date") ? 
          parseInt(h3.getAttribute("add_date") || "0") : undefined,
        id: uuidv4() // Assign a unique ID for mapping
      };
      
      console.log(`Created folder node: ${folderName}`);
      
      // Store reference to this folder node
      folderMap.set(h3, folderNode);
      elementToNodeMap.set(h3, folderNode);
    });
    
    // Build the folder hierarchy with improved parent detection
    folderHeaders.forEach((h3) => {
      const folderNode = folderMap.get(h3);
      if (!folderNode) return;
      
      // Get the containing DT element
      const parentDT = h3.closest("dt");
      if (!parentDT) {
        console.log(`No parent DT found for folder: ${folderNode.title}`);
        if (!rootNode.children) rootNode.children = [];
        rootNode.children.push(folderNode);
        return;
      }
      
      // Find the DL that directly contains this DT
      const directDL = parentDT.parentElement;
      if (!directDL || directDL.tagName !== 'DL') {
        console.log(`No direct DL found for folder: ${folderNode.title}, adding to root`);
        if (!rootNode.children) rootNode.children = [];
        rootNode.children.push(folderNode);
        return;
      }
      
      // Find the parent H3 of this DL
      const parentDL = dlParentMap.get(directDL);
      if (!parentDL) {
        // This is a top-level folder
        console.log(`Folder "${folderNode.title}" is at top level, adding to root`);
        if (!rootNode.children) rootNode.children = [];
        folderNode.originalPath = `${rootNode.title} > ${folderNode.title}`;
        rootNode.children.push(folderNode);
      } else {
        // Find the DT and then H3 that owns the parent DL
        let parentH3: Element | null = null;
        let parentDT = parentDL.parentElement;
        
        if (parentDT && parentDT.tagName === 'DT') {
          // This DL is inside a DT, check for H3 in the same DT
          parentH3 = parentDT.querySelector('h3');
        }
        
        if (parentH3 && folderMap.has(parentH3)) {
          const parentFolderNode = folderMap.get(parentH3)!;
          if (!parentFolderNode.children) parentFolderNode.children = [];
          
          // Update level and path
          folderNode.level = (parentFolderNode.level || 0) + 1;
          folderNode.originalPath = `${parentFolderNode.originalPath} > ${folderNode.title}`;
          
          parentFolderNode.children.push(folderNode);
          console.log(`Added folder "${folderNode.title}" as child of "${parentFolderNode.title}"`);
        } else {
          // Fallback - add to root if no valid parent found
          if (!rootNode.children) rootNode.children = [];
          folderNode.originalPath = `${rootNode.title} > ${folderNode.title}`;
          rootNode.children.push(folderNode);
          console.log(`Unable to find parent folder for "${folderNode.title}", adding to root`);
        }
      }
    });
    
    // Process all the bookmarks (A elements) with improved folder assignment
    const linkElements = doc.querySelectorAll("a");
    console.log(`Found ${linkElements.length} links (bookmarks)`);
    
    linkElements.forEach((link) => {
      const bookmarkTitle = link.textContent?.trim() || "Unnamed Bookmark";
      const bookmarkUrl = link.getAttribute("href") || "";
      
      // Find the DL element that contains this link
      const parentDT = link.closest("dt");
      if (!parentDT) {
        console.log(`No parent DT found for bookmark: ${bookmarkTitle}, skipping`);
        return;
      }
      
      const containingDL = parentDT.parentElement;
      if (!containingDL || containingDL.tagName !== 'DL') {
        console.log(`No containing DL found for bookmark: ${bookmarkTitle}, adding to root`);
        if (!rootNode.children) rootNode.children = [];
        rootNode.children.push({
          title: bookmarkTitle,
          url: bookmarkUrl,
          dateAdded: link.getAttribute("add_date") ? 
            parseInt(link.getAttribute("add_date") || "0") : undefined,
          originalPath: rootNode.title
        });
        return;
      }
      
      // Find the parent folder by traversing up to find the H3 that owns this DL
      let ownerH3: Element | null = null;
      let ownerDT = containingDL.parentElement;
      
      if (ownerDT && ownerDT.tagName === 'DT') {
        // This DL is inside a DT, check for H3 in the same DT
        ownerH3 = ownerDT.querySelector('h3');
      }
      
      let parentFolderNode: BookmarkNode | null = null;
      if (ownerH3 && folderMap.has(ownerH3)) {
        parentFolderNode = folderMap.get(ownerH3)!;
        console.log(`Found parent folder "${parentFolderNode.title}" for bookmark "${bookmarkTitle}"`);
      }
      
      if (!parentFolderNode) {
        // No specific parent folder found, use root
        if (!rootNode.children) rootNode.children = [];
        rootNode.children.push({
          title: bookmarkTitle,
          url: bookmarkUrl,
          dateAdded: link.getAttribute("add_date") ? 
            parseInt(link.getAttribute("add_date") || "0") : undefined,
          originalPath: rootNode.title
        });
        console.log(`No parent folder found for bookmark "${bookmarkTitle}", added to root`);
      } else {
        // Add to the correct parent folder
        if (!parentFolderNode.children) parentFolderNode.children = [];
        parentFolderNode.children.push({
          title: bookmarkTitle,
          url: bookmarkUrl,
          dateAdded: link.getAttribute("add_date") ? 
            parseInt(link.getAttribute("add_date") || "0") : undefined,
          originalPath: parentFolderNode.originalPath
        });
        console.log(`Added bookmark "${bookmarkTitle}" to folder "${parentFolderNode.title}"`);
      }
    });
    
    // Verify and log the structure
    logFolderStructure(rootNode);
    
    return rootNode;
  } catch (error: any) {
    console.error("Error parsing bookmark HTML:", error);
    return null;
  }
};

/**
 * Helper function to log the folder structure for debugging
 */
const logFolderStructure = (node: BookmarkNode, depth: number = 0) => {
  const indent = '  '.repeat(depth);
  console.log(`${indent}${node.folderType ? '📁' : '🔖'} ${node.title}${node.url ? ` (${node.url})` : ''}`);
  
  if (node.children) {
    for (const child of node.children) {
      logFolderStructure(child, depth + 1);
    }
  }
};

/**
 * Flattens the bookmark tree structure into collections
 * with improved hierarchy preservation
 */
export const flattenBookmarkCollections = (
  bookmarkTree: BookmarkNode,
  parentId: string | null = null,
  level: number = 0,
  position: number = 0
): FlattenedCollection[] => {
  const collections: FlattenedCollection[] = [];
  
  // Only process folder nodes
  if (bookmarkTree.folderType) {
    // Create a collection for this folder
    const id = bookmarkTree.id || uuidv4();
    collections.push({
      id,
      name: bookmarkTree.title,
      parent: parentId,
      level,
      position,
      originalPath: bookmarkTree.originalPath || bookmarkTree.title
    });
    
    console.log(`Creating collection for folder: ${bookmarkTree.title}, level: ${level}, path: ${bookmarkTree.originalPath}`);
    
    // Process children if they exist
    if (bookmarkTree.children) {
      let childPosition = 0;
      bookmarkTree.children.forEach((child) => {
        if (child.folderType) {
          // Recursively process child folders
          const childCollections = flattenBookmarkCollections(
            child,
            id,
            level + 1,
            childPosition++
          );
          collections.push(...childCollections);
        }
      });
    }
  }
  
  return collections;
};

/**
 * Creates collections in the database based on the flattened structure
 */
export const createCollections = async (
  collections: FlattenedCollection[],
  userId: string
): Promise<Map<string, string>> => {
  // Map to store temporary uuid to actual database id mapping
  const idMap = new Map<string, string>();
  
  try {
    console.log(`Creating collections for user ${userId}: ${collections.length} collections total`);
    
    // First, fetch all existing collections for this user
    const { data: existingCollections, error: fetchError } = await supabase
      .from("collections")
      .select("id, name")
      .eq("user_id", userId);
    
    if (fetchError) {
      console.error("Error fetching existing collections:", fetchError);
      throw fetchError;
    }
    
    console.log(`Found ${existingCollections?.length || 0} existing collections`);
    
    // Create a map of collection names to IDs for quick lookups
    const existingCollectionMap = new Map<string, string>();
    existingCollections?.forEach(collection => {
      existingCollectionMap.set(collection.name.toLowerCase(), collection.id);
    });
    
    // Sort collections by level to ensure parents are created first
    const sortedCollections = [...collections].sort((a, b) => a.level - b.level);
    
    // Collections to insert (only those that don't already exist)
    const collectionsToInsert = [];
    
    // Process each collection
    for (const collection of sortedCollections) {
      const { id: tempId, name, position } = collection;
      
      // Check if collection with this name already exists (case insensitive)
      if (existingCollectionMap.has(name.toLowerCase())) {
        // Use the existing collection ID
        const existingId = existingCollectionMap.get(name.toLowerCase())!;
        idMap.set(tempId, existingId);
        console.log(`Using existing collection: ${name} (${existingId})`);
      } else {
        // Add to the list of collections to insert
        collectionsToInsert.push({
          name,
          position,
          user_id: userId,
        });
        console.log(`Will create new collection: ${name} (temp ID: ${tempId})`);
      }
    }
    
    console.log(`Need to create ${collectionsToInsert.length} new collections`);
    
    // Insert collections in batch if there are any to insert
    if (collectionsToInsert.length > 0) {
      const { data, error } = await supabase
        .from("collections")
        .insert(collectionsToInsert)
        .select("id, name");
      
      if (error) {
        console.error("Error inserting collections:", error);
        throw error;
      }
      
      console.log(`Successfully created ${data?.length || 0} new collections`);
      
      // Map new collection names to their IDs
      data?.forEach(collection => {
        // Find the temporary ID for this collection
        const matchingColl = sortedCollections.find(c => c.name.toLowerCase() === collection.name.toLowerCase());
        if (matchingColl) {
          idMap.set(matchingColl.id, collection.id);
          console.log(`Mapped collection: ${collection.name} -> ${collection.id}`);
        }
      });
    }
    
    return idMap;
  } catch (error) {
    console.error("Error creating collections:", error);
    throw error;
  }
};

/**
 * Improved function to extract bookmarks with correct collection assignment
 * Uses the folder node ID for direct mapping to collections
 */
export const extractBookmarksWithCollections = (
  bookmarkTree: BookmarkNode,
  collectionIdMap: Map<string, string>,
  defaultCollectionId: string
): BookmarkContent[] => {
  const bookmarks: BookmarkContent[] = [];
  
  // Process function to traverse the tree
  const processNode = (node: BookmarkNode, parentNodeId: string | null) => {
    // If this is a URL node, add it as a bookmark
    if (node.url) {
      // Find the collection ID to use
      const collectionId = parentNodeId 
        ? collectionIdMap.get(parentNodeId) || defaultCollectionId 
        : defaultCollectionId;
      
      bookmarks.push({
        title: node.title,
        url: node.url,
        collectionId,
        dateAdded: node.dateAdded
      });
      
      console.log(`Extracted bookmark "${node.title}" assigned to collection ID: ${collectionId}`);
      return;
    }
    
    // If this is a folder with children, process them
    if (node.folderType && node.children && node.children.length > 0) {
      const nodeId = node.id;
      
      // Get this folder's collection ID
      let thisCollectionId = nodeId ? collectionIdMap.get(nodeId) : undefined;
      if (nodeId && thisCollectionId) {
        console.log(`Processing folder "${node.title}" with collection ID: ${thisCollectionId}`);
      }
      
      // Process each child
      node.children.forEach(child => {
        processNode(child, nodeId || null);
      });
    }
  };
  
  // Start processing from the root node
  processNode(bookmarkTree, null);
  
  return bookmarks;
};

/**
 * Creates content items in the database WITHOUT individual tracking
 */
export const createBookmarkContent = async (
  bookmarks: BookmarkContent[],
  userId: string
): Promise<void> => {
  try {
    console.log(`Creating ${bookmarks.length} bookmarks for user ${userId}`);
    
    // Process bookmarks in batches to avoid overwhelming the database
    const batchSize = 20;
    const batches = Math.ceil(bookmarks.length / batchSize);
    
    for (let i = 0; i < batches; i++) {
      const batchStart = i * batchSize;
      const batchEnd = Math.min(batchStart + batchSize, bookmarks.length);
      const batch = bookmarks.slice(batchStart, batchEnd);
      
      console.log(`Processing batch ${i+1}/${batches} (${batch.length} bookmarks)`);
      
      // Create content items
      const contentItems = batch.map(bookmark => ({
        title: bookmark.title,
        url: bookmark.url,
        content_type: "webpage", // Using a valid content_type
        user_id: userId,
        description: "",
        created_at: bookmark.dateAdded 
          ? new Date(bookmark.dateAdded * 1000).toISOString() 
          : new Date().toISOString()
      }));
      
      // Insert content items
      const { data, error } = await supabase
        .from("content_items")
        .insert(contentItems)
        .select("id, title");
      
      if (error) {
        console.error("Error inserting content items:", error);
        console.error("First item that failed:", contentItems[0]);
        throw error;
      }
      
      console.log(`Successfully created ${data?.length} content items in batch ${i+1}`);
      
      // Link content items to collections
      if (data) {
        const contentCollections = data.map((content, index) => {
          const bookmark = batch[index];
          
          console.log(`Linking content "${content.title}" to collection ID: ${bookmark.collectionId}`);
          
          return {
            content_id: content.id,
            collection_id: bookmark.collectionId
          };
        }).filter(item => item.collection_id !== "");
        
        if (contentCollections.length > 0) {
          const { error: linkError } = await supabase
            .from("content_collections")
            .insert(contentCollections);
          
          if (linkError) {
            console.error("Error linking content to collections:", linkError);
            throw linkError;
          }
          
          console.log(`Successfully linked ${contentCollections.length} items to collections`);
        }
      }
    }
  } catch (error) {
    console.error("Error creating bookmark content:", error);
    throw error;
  }
};

/**
 * Main function to process HTML bookmark import with NO individual tracking
 */
export const importBookmarksFromHtml = async (
  htmlContent: string,
  userId: string,
  onProgress?: (stage: string, progress: number) => void
): Promise<{
  success: boolean;
  collectionsCreated: number;
  bookmarksImported: number;
  error?: string;
}> => {
  try {
    // Parse HTML content
    onProgress?.("Parsing HTML file", 10);
    console.log("Starting HTML bookmark import process with NO individual content tracking");
    const bookmarkTree = parseBookmarkHtml(htmlContent);
    
    if (!bookmarkTree) {
      console.error("Failed to parse bookmark file");
      throw new Error("Failed to parse bookmark file");
    }
    
    // Count total bookmarks for validation
    const totalBookmarks = countBookmarks(bookmarkTree);
    console.log(`Total bookmarks found in HTML: ${totalBookmarks}`);
    
    // Flatten collections structure
    onProgress?.("Processing bookmark structure", 20);
    console.log("Flattening bookmark structure into collections with preserved hierarchy");
    const flattenedCollections = flattenBookmarkCollections(bookmarkTree);
    console.log(`Found ${flattenedCollections.length} collections in bookmark file`);
    
    // Create collections in database
    onProgress?.("Creating collections", 40);
    console.log("Creating collections in database");
    const collectionIdMap = await createCollections(flattenedCollections, userId);
    console.log("Collections creation complete");
    
    // Determine default collection to use if needed
    let defaultCollectionId = "";
    if (flattenedCollections.length > 0) {
      const firstTempId = flattenedCollections[0].id;
      defaultCollectionId = collectionIdMap.get(firstTempId) || "";
      
      if (!defaultCollectionId) {
        console.warn("No default collection ID available. Bookmarks without a collection may be lost.");
      } else {
        console.log(`Using default collection ID: ${defaultCollectionId}`);
      }
    }
    
    // Extract bookmarks with improved collection mapping
    onProgress?.("Extracting bookmarks", 70);
    console.log("Extracting bookmarks with improved collection mapping");
    const bookmarks = extractBookmarksWithCollections(
      bookmarkTree,
      collectionIdMap,
      defaultCollectionId
    );
    console.log(`Found ${bookmarks.length} bookmarks to import`);
    
    // Log collection assignment statistics
    const collectionAssignments = bookmarks.reduce((acc, bookmark) => {
      acc[bookmark.collectionId] = (acc[bookmark.collectionId] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    console.log("Bookmark assignments by collection:", 
      Object.entries(collectionAssignments).map(([collId, count]) => ({
        collectionId: collId,
        bookmarkCount: count
      }))
    );
    
    // Create bookmark content in database WITHOUT individual tracking
    onProgress?.("Importing bookmarks", 80);
    console.log("Creating bookmark content in database with NO individual analytics tracking");
    await createBookmarkContent(bookmarks, userId);
    console.log("Bookmark content creation complete");
    
    onProgress?.("Import complete", 100);
    console.log("HTML bookmark import process complete with NO individual content tracking");
    
    return {
      success: true,
      collectionsCreated: flattenedCollections.length,
      bookmarksImported: bookmarks.length
    };
  } catch (error: any) {
    console.error("Error importing bookmarks:", error);
    return {
      success: false,
      collectionsCreated: 0,
      bookmarksImported: 0,
      error: error.message
    };
  }
};

/**
 * Count total bookmarks in the tree
 */
const countBookmarks = (node: BookmarkNode): number => {
  let count = 0;
  
  if (node.url) {
    count = 1;
  }
  
  if (node.children) {
    for (const child of node.children) {
      count += countBookmarks(child);
    }
  }
  
  return count;
};
